import { AutocompleteAdapter } from 'components/form/AutocompleteAdapter';
import { Button, ButtonGroup } from '@mui/material';
import { productService, skuMappingService } from 'services/api';
import { IdentityField } from 'components/form/IdentityField';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { IOption } from 'interfaces';
import { IsTenantAdministrator } from 'components/helpers/IsTenantAdministrator';
import { loadingActions } from 'features';
import { required } from 'helpers/validators';
import { TextFieldAdapter } from 'components/form/TextFieldAdapter';
import { useAppDispatch } from 'app/store';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import CollapsableColumns from 'components/common/CollapsableColumns';
import { EnhancedDataGrid } from 'components/common/enhanced/data-grid';
import ReactFinalForm from 'components/form/ReactFinalForm';
import TenantPage from 'components/common/TenantPage';
import { filterOperators, numberFilterOperators } from '../../../constants';
import { baseCell } from 'components/common/enhanced/cells/base-cell';
import DataGridCell from 'components/common/DataGridCell';
import { isTechnician } from 'components/helpers/IsTechnician';

export const ProductsSkuMapping = () => {
  const { t } = useTranslation();
  const translationPrefix = 'pages.sku';
  const dispatch = useAppDispatch();
  const datagridRefresh = useRef(null);
  const isDisableEdit = isTechnician();

  const columns: GridColDef[] = [
    {
      field: 'sku',
      headerName: t(`${translationPrefix}.entity.sku`),
      filterOperators,
      width: 200,
      editable: !isDisableEdit,
      renderCell: (params: GridRenderCellParams<string>) => (
        <DataGridCell content={params.value} sx={{ fontWeight: 'bold' }} />
      )
    },
    {
      field: 'product',
      headerName: t(`global.product`),
      filterOperators,
      width: 300,
      renderCell: baseCell
    },
    {
      field: 'description',
      headerName: t(`global.description`),
      filterOperators,
      flex: 1,
      renderCell: baseCell
    },
    {
      field: 'gtin_product_batch_max_chars',
      headerName: t(`entities.contract.gtin_product_batch_max_chars`),
      filterOperators: numberFilterOperators,
      editable: !isDisableEdit,
      flex: 1,
      renderCell: baseCell
    }
  ];

  const [openDialog, setOpenDialog] = useState(false);
  const [products, setProducts] = useState<IOption[]>([]);

  useEffect(() => {
    dispatch(loadingActions.startLoading());
    productService.getAllBaseInformation().then((res) => {
      setProducts(res);
      dispatch(loadingActions.stopLoading());
    });
  }, []);

  const onSubmitCreate = async (values) => {
    dispatch(loadingActions.startLoading());
    skuMappingService.add(values).then(() => {
      location.reload;
      datagridRefresh.current();
    });
    dispatch(loadingActions.stopLoading());
  };

  const onOpenDialog = () => {
    setOpenDialog(!openDialog);
  };

  return (
    <>
      <TenantPage
        title={t(`${translationPrefix}.title`)}
        subtitle={t(`${translationPrefix}.subtitle`)}
        menuRight={
          <div className="flex justify-end items-center">
            <ButtonGroup>
              <IsTenantAdministrator>
                <Button onClick={onOpenDialog}>
                  <AddIcon />
                  {t(`${translationPrefix}.add`)}
                </Button>
              </IsTenantAdministrator>
            </ButtonGroup>
          </div>
        }>
        <CollapsableColumns
          expand={openDialog}
          onClose={() => {
            setOpenDialog(false);
          }}
          size="w-1/4"
          contentLeft={
            <EnhancedDataGrid
              service={skuMappingService}
              columns={columns}
              refresh={datagridRefresh}
              disableExport={true}
            />
          }
          contentRight={
            <div className="w-full">
              <ReactFinalForm onSubmit={onSubmitCreate}>
                <div className="flex flex-wrap my-4">
                  <div className="w-full py-2">
                    <IdentityField
                      name="sku"
                      component={TextFieldAdapter}
                      label={t(`${translationPrefix}.entity.sku`)}
                      type="text"
                      validate={required}
                      required
                    />
                  </div>
                  <div className="w-full py-2">
                    <IdentityField
                      name="product"
                      optionLabel="description"
                      component={AutocompleteAdapter}
                      options={products}
                      label={t(`global.product`)}
                      type="text"
                      validate={required}
                      required
                    />
                  </div>
                </div>
              </ReactFinalForm>
            </div>
          }
        />
      </TenantPage>
    </>
  );
};

export default ProductsSkuMapping;
